import { Theme } from '@busybusy/webapp-react-ui';
import { PhotoIcon } from 'assets/icons';
import classNames from 'classnames';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import DashboardCardBarChart from 'components/domain/dashboard/dashboard-card-chart/DashboardBarChart/DashboardBarChart';
import { dashboardCardBarChartGradientBlueLightPurple } from 'components/domain/dashboard/dashboard-card-chart/DashboardBarChart/constants/constants';
import DashboardContentCard from 'components/foundation/dashboard/DashboardContentCard/DashboardContentCard';
import useDashboardGraphDates from 'containers/dashboard/hooks/useDashboardGraphDates';
import { convertDashboardTimeFrameToTimeRangeType, getDashboardTimeFrameTitle } from 'containers/dashboard/util/utils';
import { getDashboardNavigationRange } from 'containers/scheduling/utils/utils';
import { gql } from 'graphql-request';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import usePhotoVerificationReportNavigation from 'hooks/navigation/usePhotoVerificationReportNavigation';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClassName } from 'types/ClassName';
import ITimeRange from 'types/TimeRange';
import PhotoType from 'types/enum/PhotoType';
import { useFeatureFlags } from 'utils/features';
import DashboardCardSpinner from '../DashboardCardSpinner/DashboardCardSpinner';
import DashboardProUpgradeCard from '../DashboardProUpgradeCard/DashboardProUpgradeCard';
import useDashboardCardTotal from '../hooks/useDashboardCardTotal';

export interface IPhotoVerificationDashboardCardProps {
  className?: ClassName;
  theme: Theme.DARK | Theme.LIGHT;
  setting: IDashboardSettingsItem;
  onHide: () => void;
}

const PhotoVerificationDashboardCard = (props: IPhotoVerificationDashboardCardProps) => {
  const { className, theme, setting, onHide } = props;
  const timeFrame = setting.options?.time;
  const { timeRange, timeRanges } = useDashboardGraphDates(timeFrame !== 'daily' ? timeFrame : 'pastSeven')!;
  const [barCounts, setBarCounts] = useState<IFailedPhotoVerificationCount[] | null>(null);
  const [loading, setLoading] = useState(false);

  const zippedValues = timeRanges.map((range, index) => ({
    day: range.startTime,
    value: barCounts?.[index]?.count ?? 0,
  }));

  const total = useDashboardCardTotal(
    timeRanges,
    null,
    null,
    zippedValues,
    setting.options?.time ?? 'payPeriod',
    false
  );
  const photoVerificationNavigation = usePhotoVerificationReportNavigation();
  const isPro = useFeatureFlags('PRO');

  const getData = usePhotoVerificationDashboardCardData(timeRanges);

  useEffect(() => {
    if (!isPro) return;
    setLoading(true);
    getData()
      .then((data) => setBarCounts(data))
      .catch(() => setBarCounts(null))
      .finally(() => setLoading(false));
  }, [getData, isPro]);

  const handlePhotoVerificationNavigation = () => {
    const navRange = getDashboardNavigationRange(timeRange, timeRanges, setting.options?.time ?? 'payPeriod', 'utc');

    photoVerificationNavigation({
      start_date: navRange.startTime.toISODate(),
      end_date: navRange.endTime.toISODate(),
      time_range_type: convertDashboardTimeFrameToTimeRangeType(setting.options?.time ?? 'payPeriod'),
      photo_type: PhotoType.FAILED,
    });
  };

  const classes = classNames('photo-verification-dashboard-card', className);

  return (
    <>
      {isPro ? (
        <DashboardContentCard
          header={total}
          theme={theme}
          subheaderValue={getDashboardTimeFrameTitle(
            t('Photo Verification (Failed Matches)'),
            setting.options?.time ?? 'monthly'
          )}
          className={classes}
          rightLabel={t('Details')}
          onRightLabelClick={handlePhotoVerificationNavigation}
        >
          {loading ? (
            <DashboardCardSpinner />
          ) : (
            <DashboardCardBarChart
              data={zippedValues}
              dataKey="value"
              valueType="raw"
              timeFrame={timeFrame}
              theme={theme}
              {...dashboardCardBarChartGradientBlueLightPurple}
            />
          )}
        </DashboardContentCard>
      ) : (
        <DashboardProUpgradeCard title={setting.title} svg={PhotoIcon} theme={theme} onHide={onHide} />
      )}
    </>
  );
};

// Backed by the custom `failedPhotoVerificationCounts` resolver, which drives the query off the org's
// members/time-entries (date-pruned) instead of scanning all rekognition rows in the window, and
// counts each requested bar. We send the card's exact bar ranges so org-specific boundaries (pay
// periods, week starts) are honored, and the server applies the deleted/similarity/permission filters.
const PHOTO_VERIFICATION_DASHBOARD_CARD_QUERY = gql`
  query PhotoVerificationDashboardCard($ranges: [DashboardRangeInput!]!) {
    dashboard {
      failedPhotoVerificationCounts(ranges: $ranges) {
        startTime
        endTime
        count
      }
    }
  }
`;

interface IFailedPhotoVerificationCount {
  startTime: string;
  endTime: string;
  count: number;
}

interface IPhotoVerificationDashboardCardData {
  dashboard: {
    failedPhotoVerificationCounts: IFailedPhotoVerificationCount[];
  };
}

export function usePhotoVerificationDashboardCardData(timeRanges: Array<ITimeRange<number>>) {
  const graphQLClient = useGraphQLClient();

  // `timeRanges` from useDashboardGraphDates gets a fresh identity on each mount-time re-render — its
  // populators depend on `organization`, which loads asynchronously — even when the values are
  // unchanged. Key on the values so we don't refire the query on every settling render.
  const rangesKey = JSON.stringify(timeRanges);
  const ranges = useMemo(
    () =>
      timeRanges.map((range) => ({
        startTime: DateTime.fromSeconds(range.startTime, { zone: 'utc' }).toISO({ suppressMilliseconds: true }),
        endTime: DateTime.fromSeconds(range.endTime, { zone: 'utc' }).toISO({ suppressMilliseconds: true }),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangesKey]
  );

  return useCallback(async (): Promise<IFailedPhotoVerificationCount[]> => {
    const result = await graphQLClient.request<IPhotoVerificationDashboardCardData>({
      document: PHOTO_VERIFICATION_DASHBOARD_CARD_QUERY,
      variables: { ranges },
    });

    return result.dashboard.failedPhotoVerificationCounts;
  }, [graphQLClient, ranges]);
}

export default PhotoVerificationDashboardCard;
